import axios from 'axios';
import produce from 'immer';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Slide, toast } from 'react-toastify';
import { config } from '../../config/Config';
import '../../css/datatable.css';
import '../../css/ops-management.css';
import { buildQueryString } from '../Orders/Orders';
import ModalResetPassword from './ModalResetPassword';
import ModalUpdatePassword from './ModalUpdatePassword';

toast.configure();

const ExpandableRow = ({
  toggleActivate,
  data,
  refresh,
  onShowModalUpdatePassword,
  setUserId,
  onShowModalPassword,
}) => {
  const { t } = useTranslation();

  const onGeneratePassword = async () => {
    try {
      const response = await axios.put(
        `${config.API_URL.AUTH.PASSWORD}/${data?._id}/reset`,
        {},
        { headers: config.headers }
      );
      if (response.status === 200) {
        onShowModalPassword(response.data.password);
      }
    } catch (error) {}
  };

  const handleMessage = (type = 'success', message = '') => {
    if (type === 'success') {
      return toast.success(message, {
        position: toast.POSITION.BOTTOM_LEFT,
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        transition: Slide,
      });
    }
    return toast.error(message, {
      position: toast.POSITION.BOTTOM_LEFT,
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      transition: Slide,
    });
  };
  const onClickResetPassword = () => {
    setUserId(data?._id);
    onShowModalUpdatePassword();
  };

  const activate = async (id_number) => {
    await axios
      .patch(
        `${config.API_URL.AUTH.LIST_OPERATORS}/${id_number}/active`,
        {},
        { headers: config.headers }
      )
      .then((response) => {
        if (response.data.status === 200) {
          handleMessage(
            'success',
            `${t('ops_management.action.notification.activate')} - ${data?.email}`
          );
          toggleActivate(id_number, 'true');
        }
      })
      .catch((error) => {
        const errorList = error?.response?.data?.errors;
        if (errorList) {
          errorList.forEach((error) => handleMessage('error', error?.msg));
        }
      });
  };
  const deactivate = async (id_number) => {
    if (!window.confirm('هل تريد ايقاف الحساب')) return;
    await axios
      .patch(
        `${config.API_URL.AUTH.LIST_OPERATORS}/${id_number}/deactivate`,
        {},
        { headers: config.headers }
      )
      .then((response) => {
        if (response.data.status === 200) {
          handleMessage(
            'success',
            `${t('ops_management.action.notification.deactivate')} - ${data?.email}`
          );
          toggleActivate(id_number);
        }
      })
      .catch((error) => {
        const errorList = error?.response?.data?.errors;
        if (errorList) {
          errorList.forEach((error) => handleMessage('error', error?.msg));
        }
      });
  };

  return (
    <div className="flex items-center gap-x-4 expand">
      <Link to={`/ops-management/edit/${data?._id}`}>
        <button type="button" className="edit-btn disable">
          {t('ops_management.action.edit.title')}
        </button>
      </Link>
      <button type="button" className="toggle-btn   bg-green-400" onClick={onClickResetPassword}>
        {t('edit_password')}
      </button>
      <button type="button" className="toggle-btn bg-blue-400" onClick={onGeneratePassword}>
        {t('generate_password')}
      </button>
      {data.status !== 'ACTIVE' ? (
        <button type="button" className="toggle-btn active" onClick={() => activate(data._id)}>
          {t('ops_management.action.activate')}
        </button>
      ) : (
        <button type="button" className="toggle-btn disable" onClick={() => deactivate(data._id)}>
          {t('ops_management.action.deactivate')}
        </button>
      )}
    </div>
  );
};

const OpsManagement = () => {
  const tableHeight = window.innerHeight - 260;
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState([]);
  // const [selected, setSelected] = useState([]);
  const [limit, setLimit] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [curFilters, setCurFilters] = useState({});

  const [isLoading, setIsLoading] = useState(true);

  const [userId, setUserId] = useState('');
  const [isOpenModalUpdatePassword, setIsOpenModalUpdatePassword] = useState(false);
  const [isOpenModalPassword, setIsOpenModalPassword] = useState(false);
  const [password, setPassword] = useState('');

  const onHideModalPassword = () => {
    setIsOpenModalPassword(false);
    setPassword('');
  };

  const onShowModalPassword = (password) => {
    setIsOpenModalPassword(true);
    setPassword(password);
  };

  useEffect(() => {
    const filter = {
      limit,
      page: currentPage,
    };
    setCurFilters(filter);
    getOpsList(filter);
  }, [limit, currentPage]);

  const onShowModalUpdatePassword = useCallback(() => {
    setIsOpenModalUpdatePassword(true);
  }, []);

  const onHiddenModalUpdatePassword = useCallback(() => {
    setIsOpenModalUpdatePassword(false);
  }, []);

  const getOpsList = async (filter) => {
    const newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      `?${buildQueryString({ ...filter })}`;
    window.history.replaceState({ path: newurl }, '', newurl);

    const { data } = await axios.get(config.API_URL.AUTH.LIST_OPERATORS, {
      params: { ...filter },
      headers: config.headers,
    });
    setIsLoading(false);
    setData(data?.data);
    setTotal(data?.total);
  };

  const conditionalRowStyles = [
    {
      when: (row) => selectedId.includes(row._id),
      style: {
        backgroundColor: '#C32E52',
        color: '#fff',
      },
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handler = useCallback(() => debounce((filters) => getOpsList(filters), 500), []);

  const refresh = () => {
    const newFilters = {
      filters: {
        ...curFilters,
        // keyword: filterText,
      },
      limit: limit,
      page: currentPage,
    };
    getOpsList(newFilters);
  };

  const RefreshData = () => {
    return (
      <div className="flex justify-end my-4 mx-4">
        <Link to="/ops-management/new">
          <button type="button" className="refresh-button">
            {t('ops_management.title')}
          </button>
        </Link>
        <div className="h-full flex items-center gap-x-4" onClick={() => refresh()}>
          <button className="refresh-button"> {t('refresh')} </button>
        </div>
      </div>
    );
  };

  const handlePageChange = (nextPage) => {
    if (isLoading) return false;
    if (Number(nextPage) === Number(currentPage)) return false;

    const newFilters = {
      // filters: { ...curFilters, keyword: filterText },
      limit: limit,
      page: nextPage,
    };
    const newUrl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      `?${buildQueryString({ ...newFilters })}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
    setCurrentPage(nextPage);
    handler(newFilters);
  };

  const toggleActivate = (_id) => {
    const index = data.findIndex((i) => i._id === _id);
    const toggle = produce(data, (draft) => {
      if (draft[index].status === 'ACTIVE') {
        draft[index].status = 'DEACTIVATE';
      } else {
        draft[index].status = 'ACTIVE';
      }
    });
    setData(toggle);
  };

  const schema = useMemo(
    () => [
      {
        name: t('ops_management.register.opsId'),
        selector: 'code',
        omit: false,
        width: '150px',
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.email'),
        selector: 'email',
        omit: false,
        minWidth: '250px',
        maxWidth: '350px',
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.firstName'),
        selector: 'firstname',
        minWidth: '180px',
        omit: false,
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.lastName'),
        selector: 'lastname',
        minWidth: '180px',
        omit: false,
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.phone'),
        selector: 'phone',
        minWidth: '180px',
        maxWidth: '240px',
        omit: false,
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.role'),
        selector: 'role',
        omit: false,
        width: '120px',
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.nationalID'),
        selector: 'nationality_id',
        minWidth: '180px',
        maxWidth: '500px',
        omit: false,
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.address'),
        selector: 'address',
        minWidth: '180px',
        maxWidth: '500px',
        omit: false,
        // cell: (row) => '',
      },
      {
        name: t('ops_management.register.status'),
        selector: 'status',
        omit: false,
        width: '150px',
        cell: (row) => (
          <>
            {row?.status === 'ACTIVE' ? (
              <span className={`text-green ${selectedId.includes(row?._id) ? 'selected' : ''}`}>
                {t('ops_management.status.activated')}
              </span>
            ) : (
              <span className={`text-red ${selectedId.includes(row?._id) ? 'selected' : ''}`}>
                {t('ops_management.status.deactivated')}
              </span>
            )}
          </>
        ),
      },
    ],
    [selectedId, t]
  );

  return (
    <div>
      <RefreshData />
      <DataTable
        noDataComponent={<div style={{ padding: '24px' }}>{t('orders_no_records')}</div>}
        noHeader
        columns={schema}
        data={data}
        fixedHeader
        pagination
        contextMessage={{ singular: t('item'), plural: t('items'), message: t('selected') }}
        fixedHeaderScrollHeight={tableHeight.toString() + 'px'}
        onSelectedRowsChange={(row) => setSelectedId(row.selectedRows)}
        paginationPerPage={Number(limit)}
        paginationRowsPerPageOptions={[100, 200, 400, 600]}
        onChangeRowsPerPage={(currentRowsPerPage) => {
          setLimit(currentRowsPerPage);
          setCurrentPage(1);
        }}
        progressPending={isLoading}
        paginationTotalRows={total}
        paginationServer
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        progressComponent={<div className="wait">{t('please_wait')}</div>}
        onRowExpandToggled={(toggleState, row) => [
          toggleState
            ? setSelectedId((p) => [...p, row._id])
            : setSelectedId(selectedId.filter((i) => i !== row._id)),
        ]}
        expandableRowsComponent={
          <ExpandableRow
            toggleActivate={toggleActivate}
            data={(row) => row}
            refresh={refresh}
            onShowModalUpdatePassword={onShowModalUpdatePassword}
            setUserId={setUserId}
            onShowModalPassword={onShowModalPassword}
          />
        }
        conditionalRowStyles={conditionalRowStyles}
        paginationComponentOptions={{
          rowsPerPageText: t('total_record'),
          rangeSeparatorText: 'من',
          noRowsPerPage: false,
          selectAllRowsItem: false,
        }}
        selectableRows
        selectableRowSelected={(row) => row.isSelected}
        selectableRowsVisibleOnly
        persistTableHead
        selectableRowsHighlight
        expandableRows
        expandableRowsHideExpander
        expandOnRowClicked
        direction={'rtl'}
        customStyles={{
          headCells: {
            style: {
              fontWeight: 'bold',
              fontSize: 12,
              background: '#F9F9F9',
            },
          },
          rows: {
            style: {
              fontSize: 12,
            },
          },
        }}
      />
      <ModalUpdatePassword
        isOpenModalUpdatePassword={isOpenModalUpdatePassword}
        hideModal={onHiddenModalUpdatePassword}
        userID={userId}
        setUserId={setUserId}
      />
      <ModalResetPassword
        hideModal={onHideModalPassword}
        isShowModal={isOpenModalPassword}
        password={password}
      />
    </div>
  );
};

export default OpsManagement;
